import React, { useEffect, useState, useRef, useContext, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { Toaster } from "sonner";
import { toast } from "./utils/toast";
import { setUserTimezone } from "./utils/dateUtils";
import { dedupedGet } from "./utils/dedupedRequest";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { initMonitoring, identifyUser, clearUserIdentity, trackEvent, trackAuth } from "./utils/monitoring";
import "@/App.css";
import "./styles/material3-theme.css";
import "./styles/m3-expressive-overrides.css";
import './styles/messages.css';
import './styles/calendar.css';
import './styles/maps.css';
import './styles/button-responsive.css';

// Global axios interceptors + session-expired / server-error handlers
// live in config/axiosInterceptors.js so App.js stays focused on app logic.
import {
  setSessionExpiredHandler,
  setServerErrorHandler,
  serverErrorActive,
  clearServerErrorActive,
  installAxiosInterceptors,
  _silentEndpoints,
} from "./config/axiosInterceptors";

installAxiosInterceptors();
import './styles/contrast-fixes.css';
import './styles/modal-global.css';
import './styles/global-spacing.css';
import './styles/availability.css';
import './styles/booking-calendar.css';
import './styles/booking-display.css';
import './styles/shared-components.css';
import './styles/pwa-install.css';
import ProtectedRoute from "./components/ProtectedRoute";
import TermsModal from "./components/TermsModal";
import NotificationCenter from "./components/NotificationCenter";
import TopNav from "./components/TopNav";
import ReferralCodeModal from "./components/ReferralCodeModal";
import Heartbeat from "./components/Heartbeat";
import OnboardingWizard from "./components/OnboardingWizard";
import ErrorBoundary from "./components/ErrorBoundary";
import ServerErrorOverlay from "./components/ServerErrorOverlay";
import { ConfirmationProvider } from "./context/ConfirmationContext";
import DashboardLayout from "./components/layout/DashboardLayout";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import CookieNotice from "./components/CookieNotice";
import ImpersonationBanner from "./components/ImpersonationBanner";
import { useCapacitor } from "./hooks/useCapacitor";
import { getDeviceFingerprint, sendFingerprintToBackend } from "./utils/fingerprint";
import { useNetwork } from "./hooks/useNetwork";
import { useUiReady, dismissBootSplash } from "./hooks/useUiReady";

// Initialize monitoring (LogRocket) on app load
initMonitoring();

// TopNav wrapper - only shows for unauthenticated users
// Authenticated users get navigation from the DashboardLayout sidebar
const TopNavWrapper = React.memo(() => {
  const location = useLocation();
  const authCtx = useContext(AuthContext);
  const isAdminRoute = location.pathname.startsWith('/ti-admin');
  
  // Admin pages handle their own nav
  if (isAdminRoute) return null;
  
  // Authenticated users use sidebar navigation from DashboardLayout
  if (authCtx?.user) return null;
  
  return <TopNav />;
});

// GA4 SPA page tracking - sends pageview on every route change (excludes admin)
const GTMPageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith('/ti-admin')) return;
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }
  }, [location]);
  return null;
};

// Reset document title on route change (prevents 404 title sticking)
const TitleReset = () => {
  const location = useLocation();
  useEffect(() => {
    // If the title was set to "Page Not Found", reset it on navigation
    // Pages that use PageSEO/Helmet will override this immediately
    if (document.title.includes('Page Not Found') || document.title.includes('Not Found')) {
      document.title = 'taskeri\u00ae - On-Demand Gig Platform';
    }
  }, [location.pathname]);
  return null;
};

// (Floating HelpWidget removed — its options now live on the /help Help Center page)

import Landing from "./pages/Landing";
import TermsPage from "./pages/TermsPage";
import BecomeTaskHero from "./pages/BecomeTaskHero";
import { BACKEND_URL } from './config/api';

// Lazy load all authenticated and heavy pages for code splitting
// Route table + lazy page imports live in routes/AppRoutes.js
import AppRoutes from "./routes/AppRoutes";

const API = `${BACKEND_URL}/api`;

// Resets on every full page load — guarantees one /auth/me check per load.
let authCheckedThisLoad = false;


// Loading fallback for lazy loaded components
const PageLoader = () => (
  <div className="page-loader" style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    background: 'var(--bg-primary, #f5f5f5)'
  }}>
    <div className="loading-spinner" style={{
      width: '40px',
      height: '40px',
      border: '3px solid #e0e0e0',
      borderTop: '3px solid #8448fc',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
  </div>
);

// ScrollToTop component - scrolls to top only on forward navigation, preserves position on back
function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    // Only scroll to top on PUSH (new navigation), not POP (back/forward)
    if (navigationType === 'PUSH') {
      window.scrollTo(0, 0);
    }
  }, [pathname, navigationType]);

  return null;
}


export const AuthContext = React.createContext(null);
export const UnreadMessagesContext = React.createContext({ unreadCount: 0, setUnreadCount: () => {} });

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState('connecting'); // 'connecting' | 'checking' | 'error'
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsUpdateInfo, setTermsUpdateInfo] = useState({ isUpdate: false, previousVersion: null, documentsToAccept: null });
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showServerError, setShowServerError] = useState(false);

  // Initialize native Capacitor features (back button, status bar, splash screen)
  useCapacitor();

  // Monitor network connectivity
  const { isOnline } = useNetwork();

  // Reveal gate: keep the shell invisible (and the boot splash up) until CSS,
  // fonts and the initial session check have settled — no flash of unstyled UI.
  const uiReady = useUiReady(loading);
  const isPublicPath = ['/', '/terms', '/privacy', '/cookie-policy', '/become-hero'].includes(window.location.pathname)
    || window.location.pathname.startsWith('/ti-admin');
  const showsGateScreen = (loading && !isPublicPath) || (loadingStatus === 'error' && !user);
  useEffect(() => {
    if (uiReady || showsGateScreen) dismissBootSplash();
  }, [uiReady, showsGateScreen]);

  // Register global session expired handler for axios interceptor
  // When session is confirmed expired, immediately clear user state AND show modal
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null);
      localStorage.setItem('has_session', 'false');
      sessionStorage.clear();
      setSessionExpired(true);
    });
    setServerErrorHandler(() => setShowServerError(true));
    return () => {
      setSessionExpiredHandler(null);
      setServerErrorHandler(null);
    };
  }, []);

  // Session health monitoring is handled by Heartbeat component (every 30s)
  // with proper soft verification via /auth/me before triggering logout.

  // Handle re-sign-in from session expired modal
  const handleSessionReAuth = () => {
    setSessionExpired(false);
    setUser(null);
    localStorage.setItem('has_session', 'false');
    sessionStorage.clear();
    window.location.href = '/';
  };

  // Check if onboarding should be shown
  const checkOnboarding = async () => {
    try {
      const response = await axios.get(`${API}/users/should-show-onboarding`, {
        withCredentials: true
      });
      if (response.data.show) {
        // Delay showing onboarding to not conflict with terms modal
        setTimeout(() => {
          setShowOnboarding(true);
        }, 1000);
      }
    } catch (error) {
      // Silent fail
    }
  };

  useEffect(() => {
    // Check for referral code in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      sessionStorage.setItem('pendingReferralCode', refCode.toUpperCase());
    }
    
    // Check for OAuth success — force session check regardless of has_session
    const authParam = urlParams.get('auth');
    if (authParam === 'success') {
      localStorage.setItem('has_session', 'true');
      window.history.replaceState(null, "", window.location.pathname);
    }
    
    // Check for OAuth error
    const errorParam = urlParams.get('error');
    if (errorParam) {
      if (errorParam === 'geo_restricted') {
        const country = urlParams.get('country') || 'your country';
        // Delay toast to ensure Toaster component is fully mounted
        setTimeout(() => {
          toast.error(
            `taskeri® is currently available in Canada only. Sign-ins from ${country} are not permitted. If you're in Canada, try disabling your VPN.`,
            { duration: 10000 }
          );
        }, 500);
      } else {
        console.log('OAuth error:', errorParam);
      }
      window.history.replaceState(null, "", "/");
      setLoading(false);
      return;
    }
    
    // Check for legacy Emergent auth session_id in URL fragment
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      const sessionId = hash.split("session_id=")[1].split("&")[0];
      window.history.replaceState(null, "", window.location.pathname);
      processSession(sessionId, window.location.pathname);
      return;
    }
    
    // Normal session check - cookie should be set by OAuth callback
    // Skip user auth check on admin routes (admin uses separate auth)
    if (!window.location.pathname.startsWith('/ti-admin')) {
      checkExistingSession();
    } else {
      setLoading(false);
    }
  }, []);

  const processSession = async (sessionId, currentPath = "/") => {
    try {
      console.log('Processing session with backend:', API);
      const response = await axios.post(
        `${API}/auth/session`,
        {},
        {
          headers: { "X-Session-ID": sessionId },
          withCredentials: true,
          timeout: 10000 // 10 second timeout
        }
      );
      setUser(response.data.user);
      localStorage.setItem('has_session', 'true');
      
      // Identify user in monitoring services
      identifyUser(response.data.user);
      trackAuth(response.data.user, 'oauth');
      
      const userData = response.data.user;
      
      // Capture and send device fingerprint for fraud detection (throttled, with retry)
      await sendFingerprintToBackend(API);
      const fingerprint = await getDeviceFingerprint();
      
      // Check if there's a pending referral code from URL
      const pendingRefCode = sessionStorage.getItem('pendingReferralCode');
      if (pendingRefCode && !userData.referred_by) {
        // Auto-apply referral code
        try {
          await axios.post(`${API}/referral/apply`, 
            { referral_code: pendingRefCode, device_fingerprint: fingerprint },
            {
              withCredentials: true,
              headers: { 'Content-Type': 'application/json' }
            }
          );
          sessionStorage.removeItem('pendingReferralCode');
          // Refresh user data
          const userResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(userResponse.data);
          // Store user's timezone for date formatting
          if (userResponse.data.timezone) {
            setUserTimezone(userResponse.data.timezone);
          }
        } catch (error) {
          console.error('Failed to auto-apply referral code:', error);
          sessionStorage.removeItem('pendingReferralCode');
        }
      } else {
        // Check if user is new and should see referral modal
        const hasSeenReferralModal = localStorage.getItem('hasSeenReferralModal');
        
        if (!userData.referred_by && !hasSeenReferralModal) {
          setShowReferralModal(true);
          localStorage.setItem('hasSeenReferralModal', 'true');
        }
      }
      
      // Check if onboarding should be shown (after referral modal)
      checkOnboarding();
      
      // Check if there's a redirect URL stored
      const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
      if (redirectUrl && redirectUrl !== '/') {
        sessionStorage.removeItem('redirectAfterLogin');
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error("Session processing failed:", error);
      console.error("Backend URL:", API);
      console.error("Error details:", error.response?.data || error.message);
      // Show user-friendly error
      alert('Login failed. Please try again or contact support if the issue persists.');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingSession = async () => {
    // The auth check always runs at least once per page load, so a stale
    // has_session flag (e.g. after admin impersonation sets a fresh cookie)
    // can never leave the app rendering as logged out.
    const hasSession = localStorage.getItem('has_session');
    if (hasSession === 'false' && authCheckedThisLoad) {
      setLoading(false);
      return;
    }
    authCheckedThisLoad = true;

    const MAX_RETRIES = 2;
    const BASE_DELAY = 3000; // 3s, 6s — ~9s total, gives server time to recover

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        setLoadingStatus(attempt === 0 ? 'checking' : 'connecting');
        const response = await dedupedGet(`${API}/auth/me`, {
          withCredentials: true,
          timeout: 8000,
        });
        setUser(response.data);
        localStorage.setItem('has_session', 'true');
        
        // Identify user in monitoring services (existing session)
        identifyUser(response.data);
        
        // Store user's timezone for date formatting
        if (response.data.timezone) {
          setUserTimezone(response.data.timezone);
        }
        
        // Capture and send device fingerprint for fraud detection (fire-and-forget, non-blocking)
        sendFingerprintToBackend(API).catch(() => {});
        const fingerprint = await getDeviceFingerprint();
        
        // Check if there's a pending referral code from URL (for existing sessions)
        const pendingRefCode = sessionStorage.getItem('pendingReferralCode');
        if (pendingRefCode && !response.data.referred_by) {
          try {
            await axios.post(`${API}/referral/apply`, 
              { referral_code: pendingRefCode, device_fingerprint: fingerprint },
              { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
            );
            sessionStorage.removeItem('pendingReferralCode');
            // Refresh user data to reflect referral
            const refreshed = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(refreshed.data);
          } catch (error) {
            console.error('Failed to auto-apply referral code:', error);
            sessionStorage.removeItem('pendingReferralCode');
          }
        }
        
        // Check onboarding for existing sessions too
        checkOnboarding();
        setLoading(false);
        return; // Success — exit retry loop
      } catch (error) {
        const status = error?.response?.status;
        // 401 = genuinely not authenticated — stop retrying
        if (status === 401) {
          localStorage.setItem('has_session', 'false');
          setLoading(false);
          return;
        }
        // Server/network error (502, 503, 504, timeout, network) — retry
        if (attempt < MAX_RETRIES) {
          const delay = BASE_DELAY * Math.pow(2, attempt);
          console.warn(`[Auth] Attempt ${attempt + 1} failed (${status || error.message}), retrying in ${delay}ms...`);
          setLoadingStatus('connecting');
          await new Promise(r => setTimeout(r, delay));
        } else {
          // All retries exhausted — show error state with manual retry
          console.error('[Auth] All session check attempts failed:', error.message);
          setLoadingStatus('error');
          setLoading(false);
        }
      }
    }
  };

  // Global axios interceptor — handles 503 (server starting up) with auto-retry.
  // NOTE: 401 handling is done by the module-level interceptor (line ~78) which does
  // a soft /auth/me verification before logging out — preventing false logouts on transient errors.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || '';

        // 503 — server starting up, auto-retry once after 3s
        // Skip for polling/background endpoints — they retry on their own schedule
        const isSilent = _silentEndpoints.some(ep => url.includes(ep));
        if (status === 503 && error.config && !error.config._retried503 && !isSilent) {
          error.config._retried503 = true;
          await new Promise(r => setTimeout(r, 3000));
          return axios(error.config);
        }

        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Check terms consent after user loads
  useEffect(() => {
    if (user && !termsChecked) {
      checkTermsConsent();
    }
  }, [user]);

  // Fetch initial unread message count and setup WebSocket for real-time updates
  const globalWsRef = useRef(null);
  const globalWsReconnectRef = useRef(null);
  const recentMessageIds = useRef(new Set()); // Track recent message IDs to prevent duplicate notifications
  
  // Fetch unread count from API — with error backoff
  const msgErrorCountRef = useRef(0);
  const fetchUnreadCount = async () => {
    if (!user || serverErrorActive) return;
    try {
      const response = await dedupedGet(`${API}/messages/unread-count`, {
        withCredentials: true,
        timeout: 8000
      });
      setUnreadCount(response.data.unread_count || 0);
      msgErrorCountRef.current = 0; // Reset on success
    } catch (error) {
      msgErrorCountRef.current++;
    }
  };
  
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    
    // Fetch initial unread count
    fetchUnreadCount();
    
    // Refresh message unread count — backs off on consecutive errors
    const BASE_INTERVAL = 130000; // 130s normal
    const refreshInterval = setInterval(() => {
      // Exponential backoff: 130s → 260s → 520s (caps at 3 errors)
      const backoff = Math.min(msgErrorCountRef.current, 3);
      if (backoff > 0) {
        const skip = Math.random() < (1 - 1 / Math.pow(2, backoff));
        if (skip) return; // Probabilistically skip this cycle
      }
      fetchUnreadCount();
    }, BASE_INTERVAL);
    
    // Refresh on page visibility change — throttled to prevent burst with other pollers
    let visibilityDebounce = null;
    let lastVisibilityFetch = 0;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        // Skip if we fetched within the last 30s (prevents burst)
        if (now - lastVisibilityFetch < 30000) return;
        clearTimeout(visibilityDebounce);
        visibilityDebounce = setTimeout(() => {
          lastVisibilityFetch = Date.now();
          fetchUnreadCount();
        }, 2000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    const wsUrl = BACKEND_URL.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:').replace(/\/$/, '');
    
    let wsReconnectAttempts = 0;
    let fastCloseCount = 0;
    const MAX_WS_RECONNECTS = 5; // Reduced from 10
    const MAX_FAST_CLOSES = 3; // Circuit breaker
    let isMounted = true;
    
    const connect = () => {
      if (!isMounted) return;
      if (wsReconnectAttempts >= MAX_WS_RECONNECTS) return;
      
      // Circuit breaker: stop if WS keeps dying instantly
      if (fastCloseCount >= MAX_FAST_CLOSES) {
        console.warn('[App WS] Circuit breaker: WebSocket unsupported, using polling only');
        return;
      }
      
      // Skip if on Messages page (Messages.js has its own WS connection)
      if (window.location.pathname.startsWith('/messages')) return;
      
      if (globalWsRef.current && globalWsRef.current.readyState === WebSocket.OPEN) {
        globalWsRef.current.close();
      }
      
      const openedAt = Date.now();
      
      try {
        const ws = new WebSocket(`${wsUrl}/api/ws/chat/${user.id}`);
        globalWsRef.current = ws;
        
        ws.onopen = () => {
          wsReconnectAttempts = 0;
          fastCloseCount = 0;
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'message' || data.type === 'new_message') {
              const message = data.message;
              const messageId = message?.id;
              const isFromOther = message?.sender_id !== user?.id;
              
              if (messageId && recentMessageIds.current.has(messageId)) {
                return;
              }
              
              if (messageId) {
                recentMessageIds.current.add(messageId);
                setTimeout(() => recentMessageIds.current.delete(messageId), 10000);
              }
              
              if (isFromOther) {
                fetchUnreadCount();
              }
            }
          } catch (err) {
            // Ignore
          }
        };
        
        ws.onerror = () => {};
        
        ws.onclose = () => {
          if (!isMounted) return;
          
          // Circuit breaker: detect instant closes
          const duration = Date.now() - openedAt;
          if (duration < 3000) {
            fastCloseCount++;
          }
          
          if (fastCloseCount >= MAX_FAST_CLOSES) return;
          
          wsReconnectAttempts++;
          const delay = Math.min(5000 * Math.pow(2, wsReconnectAttempts - 1), 120000);
          globalWsReconnectRef.current = setTimeout(connect, delay);
        };
      } catch (err) {
        if (!isMounted) return;
        wsReconnectAttempts++;
        const delay = Math.min(5000 * Math.pow(2, wsReconnectAttempts - 1), 120000);
        globalWsReconnectRef.current = setTimeout(connect, delay);
      }
    };
    
    connect();
    
    return () => {
      isMounted = false;
      clearInterval(refreshInterval);
      clearTimeout(visibilityDebounce);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (globalWsReconnectRef.current) clearTimeout(globalWsReconnectRef.current);
      if (globalWsRef.current) {
        globalWsRef.current.onclose = null;
        globalWsRef.current.onerror = null;
        if (globalWsRef.current.readyState === WebSocket.OPEN || globalWsRef.current.readyState === WebSocket.CONNECTING) {
          globalWsRef.current.close();
        }
      }
    };
  }, [user]);

  const checkTermsConsent = async () => {
    try {
      // Skip terms check for admin routes - admins use separate auth
      if (window.location.pathname.startsWith('/ti-admin')) {
        setTermsChecked(true);
        return;
      }
      
      const response = await axios.get(`${API}/terms/user-consent`, {
        withCredentials: true
      });
      
      // If user has completed onboarding terms, skip the modal
      if (user?.onboarding_terms_completed || user?.terms_accepted) {
        setTermsChecked(true);
        return;
      }
      
      // Skip for admin users (if they log in as regular user too)
      if (user?.is_admin || user?.role === 'admin') {
        setTermsChecked(true);
        return;
      }
      
      if (response.data.requires_consent) {
        // Check if this is an update (user had previously accepted a different version)
        const isUpdate = response.data.reason === "version_mismatch";
        setTermsUpdateInfo({
          isUpdate: isUpdate,
          previousVersion: response.data.user_version,
          documentsToAccept: response.data.documents_to_accept || null
        });
        setShowTermsModal(true);
      }
      setTermsChecked(true);
    } catch (error) {
      console.error("Error checking terms consent:", error);
      setTermsChecked(true);
    }
  };

  const handleTermsAccept = () => {
    setShowTermsModal(false);
    setTermsChecked(true);
  };

  const handleTermsDecline = async () => {
    // Log out user if they decline
    await logout();
    setShowTermsModal(false);
    window.location.href = "/";
  };

  const logout = async () => {
    try {
      // Clear user identity from monitoring services
      clearUserIdentity();
      trackEvent('user_logout');
      
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      // Always clear state and redirect, even if API call fails
      clearUserIdentity();
      setUser(null);
      localStorage.removeItem('session_token');
      localStorage.setItem('has_session', 'false');
      sessionStorage.clear();
      window.location.href = "/";
    }
  };

  // Get reCAPTCHA site key from environment
  // Server unreachable — auto-retry silently every 15 seconds (keep error screen visible)
  const autoRetryRef = useRef(null);
  useEffect(() => {
    if (loadingStatus === 'error' && !user) {
      autoRetryRef.current = setInterval(async () => {
        try {
          const response = await dedupedGet(`${API}/auth/me`, {
            withCredentials: true,
            timeout: 8000,
          });
          // Server is back — restore session
          setUser(response.data);
          localStorage.setItem('has_session', 'true');
          setLoadingStatus('checking');
          setLoading(false);
          clearInterval(autoRetryRef.current);
        } catch (error) {
          if (error?.response?.status === 401) {
            // Server is back but no session
            localStorage.setItem('has_session', 'false');
            setLoadingStatus('checking');
            setLoading(false);
            clearInterval(autoRetryRef.current);
          }
          // Otherwise keep retrying silently
        }
      }, 15000);
      return () => clearInterval(autoRetryRef.current);
    }
  }, [loadingStatus, user]);

  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  if (loading) {
    // On the landing page, don't block with loading screen — show the page immediately
    // Admin routes (/ti-admin*) run their own session check (admin_session cookie),
    // so they must NOT be gated behind the USER-session loading screen.
    if (isPublicPath) {
      // Skip loading screen for public pages — they don't need auth
      // The auth check continues in the background
    } else {
      return (
        <div className="loading-screen" data-testid="loading-screen">
          <div className="loading-spinner"></div>
          <p data-testid="loading-status">
            {loadingStatus === 'connecting' ? 'Connecting to server...' : 'Checking your session...'}
          </p>
        </div>
      );
    }
  }

  if (loadingStatus === 'error' && !user) {
    return (
      <div className="loading-screen" data-testid="connection-error-screen">
        <div style={{ textAlign: 'center', maxWidth: 400, padding: '0 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 16, color: 'var(--text-secondary, #666)' }}>
            <i className="fa-solid fa-wifi-slash" style={{ opacity: 0.6 }}></i>
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary, #333)' }}>
            Unable to connect
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary, #666)', marginBottom: 12, lineHeight: 1.5 }}>
            The server is starting up or temporarily unavailable. Please check back in a minute.
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-secondary, #999)', marginBottom: 24 }}>
            Auto-retrying every 15 seconds...
          </p>
          <button
            data-testid="retry-connection-btn"
            onClick={() => {
              setLoading(true);
              setLoadingStatus('connecting');
              checkExistingSession();
            }}
            style={{
              padding: '10px 28px',
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 8,
              border: 'none',
              background: 'var(--primary, #8448fc)',
              color: '#000',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.85'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            Try Again Now
          </button>
        </div>
      </div>
    );
  }

  // Wrap app content - conditionally use reCAPTCHA if key is configured
  const appContent = (
    <AuthContext.Provider value={{ user, setUser, logout, loading }}>
      <UnreadMessagesContext.Provider value={{ unreadCount, setUnreadCount, fetchUnreadCount }}>
      <ConfirmationProvider>
      <HelmetProvider>
      <BrowserRouter>
      <a href="#main-content" className="skip-to-main" data-testid="skip-to-main">Skip to main content</a>
      <ScrollToTop />
      <GTMPageTracker />
      <TitleReset />
      {showServerError && (
        <ServerErrorOverlay
          onRetry={() => { serverErrorActive = false; setShowServerError(false); }}
          onDismiss={() => { serverErrorActive = false; setShowServerError(false); }}
        />
      )}
      <DashboardLayout>
      <ImpersonationBanner />
      <main id="main-content">
      <AppRoutes user={user} />
        </main>
        </DashboardLayout>
        
        {/* Top Navigation Bar - Only for unauthenticated or admin pages */}
        <TopNavWrapper />
        
        {/* PWA Install Prompt */}
        <PWAInstallPrompt />

        {/* Cookie consent (self-hides once a choice is stored; hidden on admin routes) */}
        <CookieNotice />
        
        {/* Session Expired Modal */}
        {sessionExpired && (
          <div data-testid="session-expired-overlay" style={{
            position: 'fixed', inset: 0, zIndex: 999999,
            background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24
          }}>
            <div style={{
              background: '#fff', borderRadius: 14, maxWidth: 400, width: '100%',
              padding: '32px 28px', textAlign: 'center',
              boxShadow: '0 25px 60px rgba(0,0,0,0.2)'
            }}>
              <div style={{
                width: 48, height: 48, background: '#fef3c7', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px', color: '#d97706', fontSize: '1.4rem', fontWeight: 700
              }}>!</div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', margin: '0 0 6px' }}>
                Session Expired
              </h2>
              <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.6, margin: '0 0 20px' }}>
                Your session has ended — this usually happens after a period of inactivity. Your data is safe. Please sign in again to continue where you left off.
              </p>
              <button
                data-testid="session-expired-signin-btn"
                onClick={handleSessionReAuth}
                style={{
                  background: '#0f172a', color: '#fff', border: 'none',
                  padding: '10px 28px', borderRadius: 8, fontSize: '0.85rem',
                  fontWeight: 600, cursor: 'pointer', width: '100%',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={e => e.target.style.background = '#1e293b'}
                onMouseLeave={e => e.target.style.background = '#0f172a'}
              >
                Sign In Again
              </button>
              <button
                data-testid="session-expired-home-btn"
                onClick={() => { setSessionExpired(false); window.location.href = '/'; }}
                style={{
                  background: 'transparent', color: '#475569', border: '1px solid #e2e8f0',
                  padding: '10px 28px', borderRadius: 8, fontSize: '0.85rem',
                  fontWeight: 600, cursor: 'pointer', width: '100%', marginTop: 10,
                  transition: 'background 0.15s, border-color 0.15s'
                }}
                onMouseEnter={e => { e.target.style.background = '#f8fafc'; e.target.style.borderColor = '#cbd5e1'; }}
                onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.borderColor = '#e2e8f0'; }}
              >
                Go to Home Page
              </button>
            </div>
          </div>
        )}
        
        {/* Offline Indicator */}
        {!isOnline && (
          <div data-testid="offline-banner" style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
            background: '#dc2626', color: '#fff', textAlign: 'center',
            padding: '8px 16px', fontSize: '13px', fontWeight: 600,
            fontFamily: "'Inter', -apple-system, sans-serif",
          }}>
            You're offline — some features may be unavailable
          </div>
        )}
      </BrowserRouter>
      </HelmetProvider>
      <Toaster 
        position="top-right" 
        richColors 
        expand={false}
        closeButton
        visibleToasts={4}
        gap={8}
        style={{ zIndex: 99999 }}
        toastOptions={{
          style: {
            background: '#fff',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '12px',
            padding: '14px 18px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: "'Inter', -apple-system, sans-serif",
            lineHeight: '1.4',
            minWidth: '320px',
            maxWidth: '420px',
            backdropFilter: 'blur(12px)',
          },
          className: 'taskeri-toast',
          duration: 4000,
          success: {
            style: {
              background: '#f0fdf4',
              border: '1px solid #86efac',
              color: '#14532d',
            },
          },
          error: {
            style: {
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              color: '#7f1d1d',
            },
            duration: 6000,
          },
          warning: {
            style: {
              background: '#fffbeb',
              border: '1px solid #fcd34d',
              color: '#78350f',
            },
          },
          info: {
            style: {
              background: '#eff6ff',
              border: '1px solid #93c5fd',
              color: '#1e3a5f',
            },
          },
        }}
      />
      <Heartbeat />
      
      {/* Terms Modal */}
      {user && showTermsModal && (
        <TermsModal
          isOpen={showTermsModal}
          onAccept={handleTermsAccept}
          onDecline={handleTermsDecline}
          isUpdate={termsUpdateInfo.isUpdate}
          previousVersion={termsUpdateInfo.previousVersion}
          documentsToAccept={termsUpdateInfo.documentsToAccept}
        />
      )}
      
      {/* Referral Code Modal */}
      {user && showReferralModal && (
        <ReferralCodeModal
          isOpen={showReferralModal}
          onClose={() => setShowReferralModal(false)}
          onSuccess={() => {
            setShowReferralModal(false);
            // Refresh user data to get updated referral info
            checkExistingSession();
          }}
        />
      )}
      
      {/* Onboarding Wizard */}
      {user && showOnboarding && !showTermsModal && !showReferralModal && (
        <OnboardingWizard
          isOpen={showOnboarding}
          onClose={() => setShowOnboarding(false)}
          onComplete={() => {
            setShowOnboarding(false);
            // Refresh user data
            checkExistingSession();
          }}
        />
      )}
    </ConfirmationProvider>
    </UnreadMessagesContext.Provider>
    </AuthContext.Provider>
  );

  // Wrap with ErrorBoundary and optionally reCAPTCHA provider
  const wrappedContent = (
    <ErrorBoundary>
      <div className={`app-shell${uiReady ? ' app-shell--ready' : ''}`} data-testid="app-shell" data-ready={uiReady ? 'true' : 'false'}>
        {appContent}
      </div>
    </ErrorBoundary>
  );

  // Wrap with reCAPTCHA provider only when user is logged in (it's only needed on authenticated pages)
  if (recaptchaSiteKey && user) {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={recaptchaSiteKey}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: "body",
        }}
      >
        {wrappedContent}
      </GoogleReCaptchaProvider>
    );
  }

  return wrappedContent;
}

export default App;
